import palette from "theme/palette";
import { normalizeLabel } from "./utils";
import { ShrimpHealthColor, ShrimpHealthTarget } from "modules/notifications/api/types";

export const TRANSLATION_KEYS = {
  HEADER: {
    DATE: 'table.dateTime',
  },
  MESSAGES: {
    NO_DATA: 'noData',
    LOAD_MORE: 'loadMore',
    RELEASE_TO_LOAD: 'lab.table.releaseToLoad',
  },
} as const;

export const THRESHOLD_VALUES = {
  YELLOW_THRESHOLD: 200,
  DEFAULT_THRESHOLD: 0,
} as const;


export enum LabelGroup {
  ShrimpAge = 'อายุกุ้ง',
  Drug = 'ยา',
  Vitamin = 'วิตามิน',
  MicrobeFood = 'จุลินทรีย์ ผสมอาหาร',
  BrandFood = 'ยี่ห้ออาหาร',
  FoodPerDay = 'อาหารต่อวัน',
  FoodTotal = 'อาหารสะสม',
  Note = 'หมายเหตุ',
  ShrimpResult = 'ผลกุ้ง',
  WaterResult = 'ผลน้ำ',
  NoteOwner = 'หมายเหตุ เจ้าของฟาร์ม',
  NoteWorker = 'หมายเหตุ ลูกจ้าง',
}

export enum LabelName {
  // อายุกุ้ง
  ShrimpAge = 'อายุกุ้ง',

  // ยา
  Drug1 = '1',
  Drug2 = '2',
  Drug3 = '3',

  // วิตามิน
  Vitamin1 = '1',
  Vitamin2 = '2',
  Vitamin3 = '3',

  // จุลินทรีย์ ผสมอาหาร
  MicrobeFood1 = '1',
  MicrobeFood2 = '2',
  MicrobeFood3 = '3',

  // อาหาร
  FeedBrand = 'ยี่ห้ออาหาร',
  FeedPerDay = 'อาหารต่อวัน',
  AccumulatedFeed = 'อาหารสะสม',

  // หมายเหตุ
  Note = 'หมายเหตุ',
  NoteOwner = 'หมายเหตุ เจ้าของฟาร์ม',
  NoteEmployee = 'หมายเหตุ ลูกจ้าง',

  // ผลกุ้ง
  ShrimpLiverYellow = 'ตับเหลือง',
  ShrimpLiverGreen = 'ตับเขียว',
  ShrimpLiverBlack = 'ตับดำ',
  ShrimpLiverGreenYellow = 'ตับเขียว/เหลือง',
  ShrimpStomachYellow = 'กะเพาะเหลือง',
  ShrimpStomachGreen = 'กะเพาะเขียว',
  ShrimpStomachBlack = 'กะเพาะดำ',
  ShrimpStomachGreenYellow = 'กะเพาะเขียว/เหลือง',

  // ผลน้ำ
  WaterYellow = 'เหลือง',
  WaterGreen = 'เขียว',
  WaterBlack = 'ดำ',
  WaterGreenYellow = 'เขียว/เหลือง',
  WaterMicrobe = 'จุลินทรีย์',
  WaterPHMorning = 'pH เช้า',
  WaterPHEvening = 'pH เย็น',
  WaterDOMax = 'DO สูงสุด',
  WaterDOMin = 'DO ต่ำสุด',
  WaterTempMorning = 'อุณหภูมิเช้า (°c)',
  WaterTempEvening = 'อุณหภูมิเย็น (°c)',
  WaterAmmonia = 'แอมโมเนีย (NH3)',
  WaterNitrite = 'ไนไตรท์ (NO32-)',
  WaterAlkaline = 'อัลคาไลน์',
  WaterTurbidity = 'ความขุ่นใส (ซม.)',
  WaterLevel = 'ระดับความสูงน้ำ (ซม.)',
  WaterRefill = 'การเติมน้ำ (ซม.)',
  WaterDrain = 'การถ่ายน้ำ (ซม.)',
  WaterFlow = 'การโฟลว์น้ำ (ชั่วโมง)',
  WaterColor = 'สีน้ำ',
  WaterLime = 'ปูนขาว',
  WaterCement = 'ปูนซีเมนต์',
  WaterDye = 'สีน้ำเทียม',
  WaterMolasses = 'กากน้ำตาล',
  WaterFlocculant = 'สารจับตะกอน',
}

export enum LabelDisabledValues {
  ShrimpAge = LabelGroup.ShrimpAge,
  FoodTotal = LabelGroup.FoodTotal,
}

export enum LabelRowSpan {
  ShrimpAge = LabelGroup.ShrimpAge,
  FeedBrand = LabelGroup.BrandFood,
  FeedPerDay = LabelGroup.FoodPerDay,
  AccumulatedFeed = LabelGroup.FoodTotal,
  Note = LabelGroup.Note,
  NoteOwner = LabelGroup.NoteOwner,
  NoteEmployee = LabelGroup.NoteWorker,
}

export const LABEL_COLOR_MAP: Record<string, string> = {};
[
  // Yellow
  [LabelName.ShrimpLiverYellow, palette.light.warning.main],
  [LabelName.WaterYellow, palette.light.warning.main],
  [LabelName.ShrimpStomachYellow, palette.light.warning.main],

  // Green
  [LabelName.ShrimpLiverGreen, palette.light.primary.main],
  [LabelName.WaterGreen, palette.light.primary.main],
  [LabelName.ShrimpStomachGreen, palette.light.primary.main],

  // Black
  [LabelName.ShrimpLiverBlack, palette.light.grey[500]],
  [LabelName.WaterBlack, palette.light.grey[500]],
  [LabelName.ShrimpStomachBlack, palette.light.grey[500]],

  // Green/Yellow
  [LabelName.ShrimpLiverGreenYellow, palette.light.success.light],
  [LabelName.WaterGreenYellow, palette.light.success.light],
  [LabelName.ShrimpStomachGreenYellow, palette.light.success.light],
].forEach(([label, color]) => {
  LABEL_COLOR_MAP[normalizeLabel(label as string)] = color as string;
});

export const LABEL_TRANSLATION: Record<string, string> = {
  //group
  [LabelGroup.ShrimpAge]: 'table.ageShrimp',
  [LabelGroup.Drug]: 'table.drug',
  [LabelGroup.Vitamin]: 'table.vitamin',
  [LabelGroup.MicrobeFood]: 'table.microorganismsFoodMix',
  [LabelGroup.BrandFood]: 'table.foodBrand',
  [LabelGroup.FoodPerDay]: 'table.dailyFood',
  [LabelGroup.FoodTotal]: 'table.accumulatedFood',
  [LabelGroup.Note]: 'table.notes',
  [LabelGroup.ShrimpResult]: 'table.shrimpSummary',
  [LabelGroup.WaterResult]: 'table.waterSummary',
  [LabelGroup.NoteOwner]: 'table.remarksFarmOwner',
  [LabelGroup.NoteWorker]: 'table.remarksEmployee',

  // ผลน้ำ
  [LabelName.WaterYellow]: 'table.yellow',
  [LabelName.WaterGreen]: 'table.green',
  [LabelName.WaterBlack]: 'table.black',
  [LabelName.WaterGreenYellow]: 'table.greenYellow',
  [LabelName.WaterMicrobe]: 'table.microorganisms',

  // ผลกุ้ง
  [LabelName.ShrimpLiverYellow]: 'table.yellowLiver',
  [LabelName.ShrimpLiverGreen]: 'table.greenLiver',
  [LabelName.ShrimpLiverBlack]: 'table.blackLiver',
  [LabelName.ShrimpLiverGreenYellow]: 'table.greenYellowLiver',
  [LabelName.ShrimpStomachYellow]: 'table.yellowStomach',
  [LabelName.ShrimpStomachGreen]: 'table.greenStomach',
  [LabelName.ShrimpStomachBlack]: 'table.blackStomach',
  [LabelName.ShrimpStomachGreenYellow]: 'table.greenYellowStomach',

  // ผลน้ำอื่นๆ
  [LabelName.WaterPHMorning]: 'table.morningPh',
  [LabelName.WaterPHEvening]: 'table.eveningPh',
  [LabelName.WaterDOMax]: 'table.maximumDo',
  [LabelName.WaterDOMin]: 'table.minimumDo',
  [LabelName.WaterTempMorning]: 'table.morningTemperature',
  [LabelName.WaterTempEvening]: 'table.eveningTemperature',
  [LabelName.WaterAmmonia]: 'table.ammonia',
  [LabelName.WaterNitrite]: 'table.nitrite',
  [LabelName.WaterAlkaline]: 'table.alkali',
  [LabelName.WaterTurbidity]: 'table.turbidity',
  [LabelName.WaterLevel]: 'table.waterLevel',
  [LabelName.WaterRefill]: 'table.waterAddition',
  [LabelName.WaterDrain]: 'table.waterTransfer',
  [LabelName.WaterFlow]: 'table.waterFlow',
  [LabelName.WaterColor]: 'table.waterColor',
  [LabelName.WaterLime]: 'table.lime',
  [LabelName.WaterCement]: 'table.cement',
  [LabelName.WaterDye]: 'table.waterDye',
  [LabelName.WaterMolasses]: 'table.molasses',
  [LabelName.WaterFlocculant]: 'table.flocculator',
};

export const WATER_QUALITY_THRESHOLDS_MAP: Record<string, string> = {
  [LabelName.WaterPHMorning]: 'ph',
  [LabelName.WaterPHEvening]: 'ph',
  [LabelName.WaterTempMorning]: 'temperature',
  [LabelName.WaterTempEvening]: 'temperature',
  [LabelName.WaterDOMax]: 'do',
  [LabelName.WaterDOMin]: 'do',
  [LabelName.WaterAlkaline]: 'alkali',
  [LabelName.WaterNitrite]: 'nitrite',
  [LabelName.WaterAmmonia]: 'ammonia',
  [LabelName.WaterTurbidity]: 'turbidity',
};

export const CATEGORIES_ALLOW_REMOVE = [
  LabelGroup.Drug,
  LabelGroup.Vitamin,
  LabelGroup.MicrobeFood,
] as const;


export const TableIDMapCategory = {
  [LabelGroup.Drug]: 'medicine',
  [LabelGroup.Vitamin]: 'vitamin',
  [LabelGroup.MicrobeFood]: 'feed_additive',
}

// Every row boundary in this table is the same hairline, so the ผลกุ้ง → ผลน้ำ
// hand-off disappears into a long run of similar colour-coded rows and
// recorders lose track of which section a row belongs to. Thick rules bracket
// the ผลกุ้ง + ผลน้ำ block: above the first row of ผลกุ้ง, below the last row of
// ผลกุ้ง (the split itself) and below the last row of ผลน้ำ.
export const GROUPS_WITH_DIVIDER_ABOVE: readonly LabelGroup[] = [LabelGroup.ShrimpResult];
export const GROUPS_WITH_DIVIDER_BELOW: readonly LabelGroup[] = [
  LabelGroup.ShrimpResult,
  LabelGroup.WaterResult,
];
export const GROUP_DIVIDER_WIDTH = 4;
// Same grey as every other cell border — the weight alone carries the split,
// matching the 4px grey[300] rule that already separates the label columns
// from the data columns.
export const GROUP_DIVIDER_BORDER = `${GROUP_DIVIDER_WIDTH}px solid ${palette.light.grey[300]}`;

export const TEXT_TYPE_LABELS: LabelName[] = [
  LabelName.Note,
  LabelName.NoteOwner,
  LabelName.NoteEmployee,
  LabelName.WaterMicrobe,
];

export const ROW_HEIGHT_DEFAULT = 40;
export const ROW_HEIGHT_TEXTTYPE = 80;

// NK-273: which shrimp-health alert setting (ตั้งค่าแจ้งเตือนสุขภาพกุ้ง) colours
// each vibrio row.
export const SHRIMP_HEALTH_SETTING_BY_LABEL: Record<
  string,
  { target: ShrimpHealthTarget; color: ShrimpHealthColor }
> = {
  [LabelName.ShrimpLiverYellow]: { target: 'liver', color: 'yellow' },
  [LabelName.ShrimpLiverGreen]: { target: 'liver', color: 'green' },
  [LabelName.ShrimpLiverBlack]: { target: 'liver', color: 'black' },
  [LabelName.ShrimpLiverGreenYellow]: { target: 'liver', color: 'green_yellow' },
  [LabelName.ShrimpStomachYellow]: { target: 'stomach', color: 'yellow' },
  [LabelName.ShrimpStomachGreen]: { target: 'stomach', color: 'green' },
  [LabelName.ShrimpStomachBlack]: { target: 'stomach', color: 'black' },
  [LabelName.ShrimpStomachGreenYellow]: { target: 'stomach', color: 'green_yellow' },
  [LabelName.WaterYellow]: { target: 'water', color: 'yellow' },
  [LabelName.WaterGreen]: { target: 'water', color: 'green' },
  [LabelName.WaterBlack]: { target: 'water', color: 'black' },
  [LabelName.WaterGreenYellow]: { target: 'water', color: 'green_yellow' },
};
